import { Spacing, TextVariantWithModifiers } from "@assets/style/appMuiTheme"
import {
  css,
  cssSafeValue,
  GroovyTextColorKind as CSSGroovyTextColorKind,
  mapColorToCSSVariable,
  parseTextVariant,
  spacingClass,
  spacingVars,
  truncateClass,
  truncateVars,
} from "@assets/style/util/cssUtils"
import { Typography, TypographyProps } from "@material-ui/core"

import { Variant as MuiTypographyVariant } from "@material-ui/core/styles/createTypography"
import Skeleton, { SkeletonProps } from "@material-ui/lab/Skeleton"
import classNames from "classnames"
import {
  ElementType,
  FC,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import classes from "./DiscoText.module.css"

type NullableTextElement = HTMLSpanElement | HTMLParagraphElement | null

export type GroovyTextColorKind = CSSGroovyTextColorKind

export type DiscoTextProps = Partial<StyleProps> &
  Omit<TypographyProps, "variant" | "color"> & {
    testid?: string
    /** The number of lines to truncate text to */
    truncateText?: number
    component?: ElementType
    /** Default is 'body-md' */
    variant?: TextVariantWithModifiers
    fontStyle?: "normal" | "italic"
    textDecoration?: "none" | "underline" | "line-through"
    lineHeight?: number
  } & Spacing

const DiscoText = forwardRef<HTMLSpanElement, DiscoTextProps>(
  (
    {
      variant = "body-md",
      fontStyle,
      lineHeight,
      color,
      truncateText,
      margin = 0,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      padding = 0,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      testid,
      setColor,
      children,
      textDecoration,
      className: customClassName,
      classes: customClasses,
      style: customStyle,
      ...rest
    },
    ref
  ) => {
    // Pack spacing props into a single object
    const spacingProps = {
      margin,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      padding,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
    }

    const { variantType, weight, textTransform } = parseTextVariant(variant)

    const [isTextTruncated, setIsTextTruncated] = useState(false)
    const textRef = useRef<NullableTextElement>(null)

    // Assign the forwarded ref to the textRef (if provided)
    useImperativeHandle<NullableTextElement, NullableTextElement>(
      ref,
      () => textRef.current
    )

    useEffect(() => {
      if (!textRef.current) return
      // Text is truncated if the width of the text is less than the scroll width
      setIsTextTruncated(textRef.current.clientWidth < textRef.current.scrollWidth)
    }, [])
    return (
      <Typography
        ref={textRef}
        classes={{
          // Variant specific styles
          root: classNames(
            classes[variantType],
            classes.text,
            lineHeight && classes.lineHeight,
            spacingClass(),
            truncateClass(truncateText),
            customClassName,
            customClasses?.root
          ),
        }}
        style={
          // Define custom CSS variables for the dynamic styles
          // This is necessary for CSS values that are not known / dynamic
          css({
            // Keep any existing style
            ...customStyle,
            "--text-color":
              setColor ||
              /** Fallback to --groovy-text-primary if a custom color is not set */
              (color ? mapColorToCSSVariable(color) : "var(--groovy-text-primary)"),
            "--text-weight": weight,
            "--text-style": fontStyle || "normal",
            "--text-transform": textTransform,
            "--text-decoration": textDecoration,
            "--text-line-height": lineHeight ? cssSafeValue(lineHeight) : undefined,
            ...spacingVars(spacingProps),
            ...truncateVars(truncateText),
          })
        }
        variant={VARIANT_TO_TYPOGRAPHY[variant]}
        title={typeof children === "string" && isTextTruncated ? children : undefined}
        data-testid={testid}
        {...rest}
      >
        {children}
      </Typography>
    )
  }
)

// Memoize the component to reduce unnecessary CSS styles recalculations from re-renders
export default memo(DiscoText)

export const typographyClasses = classes

/**
 * Map each of our variants to the MUI Typography variant it uses under the hood.
 * This determines what DOM element the text is rendered as (i.e. <h1> v.s. <p>)
 *
 * Note: Disco 2.0 typography doesn't follow the Material Design typography system
 * In general:
 * `body-lg` => body1
 * `body-base`, `body-md` => body2
 * `body-xs`, `body-sm` => caption
 * `*-uppercase` => overline
 */
const VARIANT_TO_TYPOGRAPHY: Record<TextVariantWithModifiers, MuiTypographyVariant> = {
  "heading-xxl": "h1",
  "heading-xxl-500": "h1",
  "heading-xxl-600": "h1",
  "heading-xxl-700": "h1",
  "heading-xl": "h2",
  "heading-xl-500": "h2",
  "heading-xl-600": "h2",
  "heading-xl-700": "h2",
  "heading-lg": "h3",
  "heading-lg-500": "h3",
  "heading-lg-600": "h3",
  "heading-lg-700": "h3",
  "heading-md": "h4",
  "heading-md-500": "h4",
  "heading-md-600": "h4",
  "heading-md-700": "h4",
  "heading-sm": "h5",
  "heading-sm-500": "h5",
  "heading-sm-600": "h5",
  "heading-sm-700": "h5",
  "heading-xs": "h6",
  "heading-xs-500": "h6",
  "heading-xs-600": "h6",
  "heading-xs-700": "h6",
  "body-lg": "body1",
  "body-lg-500": "body1",
  "body-lg-600": "body1",
  "body-lg-700": "body1",
  "body-lg-uppercase": "overline",
  "body-lg-500-uppercase": "overline",
  "body-lg-600-uppercase": "overline",
  "body-lg-700-uppercase": "overline",
  "body-md": "body1",
  "body-md-500": "body1",
  "body-md-600": "body1",
  "body-md-700": "body1",
  "body-md-uppercase": "overline",
  "body-md-500-uppercase": "overline",
  "body-md-600-uppercase": "overline",
  "body-md-700-uppercase": "overline",
  "body-sm": "body2",
  "body-sm-500": "body2",
  "body-sm-600": "body2",
  "body-sm-700": "body2",
  "body-sm-uppercase": "overline",
  "body-sm-500-uppercase": "overline",
  "body-sm-600-uppercase": "overline",
  "body-sm-700-uppercase": "overline",
  "body-xs": "caption",
  "body-xs-500": "caption",
  "body-xs-600": "caption",
  "body-xs-700": "caption",
  "body-xs-uppercase": "overline",
  "body-xs-500-uppercase": "overline",
  "body-xs-600-uppercase": "overline",
  "body-xs-700-uppercase": "overline",
}

interface StyleProps {
  fontStyle: "normal" | "italic"
  textDecoration?: string
  color?: GroovyTextColorKind
  setColor?: string
  lineHeight?: number
}

type DiscoTextSkeletonProps = Omit<DiscoTextProps, "color"> &
  Omit<SkeletonProps, "variant">

export const DiscoTextSkeleton: FC<DiscoTextSkeletonProps> = ({
  variant = "body-md",
  margin = 0,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  padding = 0,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  className,
  ...rest
}) => {
  const { variantType } = parseTextVariant(variant)

  // Pack spacing props into a single object
  const spacingProps = {
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
  }

  return (
    <Skeleton
      className={classNames(
        classes[variantType],
        classes.text,
        spacingClass(),
        className
      )}
      style={spacingVars(spacingProps)}
      {...rest}
    />
  )
}
